import { Route, useLocation, Routes, Navigate } from 'react-router-dom';
import { Suspense, useEffect, lazy } from 'react';

import LoadingPage from './components/global/LoadingPage';
import ProtectedRoute from './components/global/ProtectedRoute';

import { useAuthContext } from './auth/useAuthContext';
import { resolveAuthenticatedEntryPath } from './utils/onboardingRoute';

// Auth Pages
const NotFound = lazy(() => import('./pages/NotFound'));
const Login = lazy(() => import('./pages/auth/Login'));
const ForgatPassword = lazy(() => import('./pages/auth/ForgatPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const Register = lazy(() => import('./pages/auth/Register'));
const OTP = lazy(() => import('./pages/auth/OTP'));
const WelcomeAccount = lazy(() => import('./pages/auth/WelcomeAccount'));
const SubscriptionPackages = lazy(() => import('./pages/auth/SubscriptionPackages'));
const ChangePassword = lazy(() => import('./pages/auth/ChangePassword'));
const RegisterShopAccount = lazy(() => import('./pages/auth/RegisterShopAccount'));
const NewUpgradeSubscribe = lazy(() => import('./pages/auth/NewUpgradeSubscribe'));
const PaymentCallback = lazy(() => import('./pages/PaymentCallback'));
const ContinueToSettings = lazy(() => import('./pages/auth/ContinueToSettings'));

// Service Provider Pages
const ServiceProvider = lazy(() => import('./pages/ServiceProvider/ServiceProvider'));
const Reviews = lazy(() => import('./pages/ServiceProvider/Reviews'));
const Messages = lazy(() => import('./pages/ServiceProvider/Messages'));
const Notifications = lazy(() => import('./pages/ServiceProvider/Notifications'));
const MainSetting = lazy(() => import('./pages/ServiceProvider/MainSetting'));
const ContactBranches = lazy(() => import('./pages/ServiceProvider/ContactBranches'));
const ContactBranch = lazy(() => import('./pages/ServiceProvider/ContactBranch'));
const TimeWork = lazy(() => import('./pages/ServiceProvider/TimeWork'));
const ContactNumbers = lazy(() => import('./pages/ServiceProvider/ContactNumbers'));
const AddNumber = lazy(() => import('./pages/ServiceProvider/AddNumber'));
const Sections = lazy(() => import('./pages/ServiceProvider/Sections'));
const AddSection = lazy(() => import('./pages/ServiceProvider/AddSection'));
const Section = lazy(() => import('./pages/ServiceProvider/Section'));
const AddCategory = lazy(() => import('./pages/ServiceProvider/AddCategory'));
const Links = lazy(() => import('./pages/ServiceProvider/links/Links'));
const AddLink = lazy(() => import('./pages/ServiceProvider/links/AddLink'));
const LibraryLinks = lazy(() => import('./pages/ServiceProvider/links/LibraryLinks'));
const AddUrlLink = lazy(() => import('./pages/ServiceProvider/links/AddUrlLink'));
const AddCustomUrlLink = lazy(() => import('./pages/ServiceProvider/links/AddCustomUrlLink'));
const CustomerActivities = lazy(() => import('./pages/ServiceProvider/CustomerActivities'));
const AddOffer = lazy(() => import('./pages/ServiceProvider/AddOffer'));
const Offers = lazy(() => import('./pages/ServiceProvider/Offers'));
const MoreFeatures = lazy(() => import('./pages/ServiceProvider/MoreFeatures'));
const SuperFasterFeatures = lazy(() => import('./pages/ServiceProvider/SuperFasterFeatures'));
const AddEffectFaster = lazy(() => import('./pages/ServiceProvider/AddEffectFaster'));
const AddOpenMessage = lazy(() => import('./pages/ServiceProvider/AddOpenMessage'));
const AddMarquee = lazy(() => import('./pages/ServiceProvider/AddMarquee'));
const AddAnimatedImage = lazy(() => import('./pages/ServiceProvider/AddAnimatedImage'));
const LinkStatistics = lazy(() => import('./pages/ServiceProvider/LinkStatistics'));
const GlobalSetting = lazy(() => import('./pages/ServiceProvider/GlobalSetting'));
const ContactUs = lazy(() => import('./pages/ServiceProvider/ContactUs'));
const AddBranch = lazy(() => import('./pages/ServiceProvider/AddBranch'));
const NetworkingSetting = lazy(() => import('./pages/ServiceProvider/NetworkingSetting'));
const EditOffer = lazy(() => import('./pages/ServiceProvider/EditOffer'));
const EditBranch = lazy(() => import('./pages/ServiceProvider/EditBranch'));
const EditSection = lazy(() => import('./pages/ServiceProvider/EditSection'));
const EditCategory = lazy(() => import('./pages/ServiceProvider/EditCategory'));
const EditProfile = lazy(() => import('./pages/ServiceProvider/EditProfile'));
const NfcOrders = lazy(() => import('./pages/ServiceProvider/NfcOrders'));
const EditUrlLink = lazy(() => import('./pages/ServiceProvider/links/EditUrlLink'));
const EditCustomUrlLink = lazy(() => import('./pages/ServiceProvider/links/EditCustomUrlLink'));
const ShoppingCart = lazy(() => import('./pages/ServiceProvider/ShoppingCart'));

// Mall Pages
const ShopsSections = lazy(() => import('./pages/ServiceProvider/mall/ShopsSections'));
const AddShopSection = lazy(() => import('./pages/ServiceProvider/mall/AddShopSection'));
const EditShopSection = lazy(() => import('./pages/ServiceProvider/mall/EditShopSection'));
const ShopsSection = lazy(() => import('./pages/ServiceProvider/mall/ShopsSection'));
const AddShop = lazy(() => import('./pages/ServiceProvider/mall/AddShop'));
const EditShop = lazy(() => import('./pages/ServiceProvider/mall/EditShop'));
const ShopsMallOffers = lazy(() => import('./pages/ServiceProvider/mall/ShopsMallOffers'));

// Layouts
const LayoutProvider = lazy(() => import('./layouts/LayoutProvider'));
const LayoutAuthProvider = lazy(() => import('./layouts/LayoutAuthProvider'));
const LayoutMarketer = lazy(() => import('./layouts/LayoutMarketer'));
const LayoutShop = lazy(() => import('./layouts/LayoutShop'));

// Marketer Pages
const Welcome = lazy(() => import('./pages/marketer/Welcome'));
const Home = lazy(() => import('./pages/marketer/Home'));
const MainSettingMarketer = lazy(() => import('./pages/marketer/MainSetting'));
const Commissions = lazy(() => import('./pages/marketer/Commissions'));
const PaidAmountsDetails = lazy(() => import('./pages/marketer/PaidAmountsDetails'));
const MarketerNotifications = lazy(() => import('./pages/marketer/MarketerNotifications'));
const ReceivingCommission = lazy(() => import('./pages/marketer/ReceivingCommission'));
const WithdrawAmount = lazy(() => import('./pages/marketer/WithdrawAmount'));
const Stores = lazy(() => import('./pages/marketer/Stores'));

// Shop Pages
const OffersShop = lazy(() => import('./pages/Shop/OffersShop'));
const AddOfferShop = lazy(() => import('./pages/Shop/AddOfferShop'));
const EditOfferShop = lazy(() => import('./pages/Shop/EditOfferShop'));

// Utils
// import { getDefaultRouteForRole } from './utils/routeUtils';

const RoutesPath = () => {
    const { user, isInitialized, isAuthenticated } = useAuthContext();

    const ScrollToTop = () => {
        const { pathname } = useLocation();
        useEffect(() => {
            window.scrollTo(0, 0);
        }, [pathname]);
        return null;
    };

    const rootTarget = !isInitialized
        ? null
        : isAuthenticated
            ? resolveAuthenticatedEntryPath({
                role: user?.role,
                store: user?.store || null,
            })
            : '/login';

    return (
        <>
            <ScrollToTop />
            <Suspense fallback={<LoadingPage />}>
                <Routes>
                    <Route
                        path="/"
                        element={
                            rootTarget ? (
                                <Navigate replace to={rootTarget} />
                            ) : (
                                <LoadingPage />
                            )
                        }
                    />

                    {/* Auth Routes */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/forgat-password" element={<ForgatPassword />} />
                    <Route path="/otp/:type" element={<OTP />} />
                    <Route path="/register/:role" element={<Register />} />
                    <Route path="/create-account-shop" element={<RegisterShopAccount />} />

                    {/* Auth Provider Layout */}
                    <Route element={<LayoutAuthProvider />}>
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/change-password" element={<ChangePassword />} />
                        <Route path="/welcome" element={<WelcomeAccount />} />
                        <Route path="/welcome-marketer" element={<Welcome />} />
                        <Route path="/subscription-packages" element={<SubscriptionPackages />} />
                        <Route path="/upgrade-subscribe" element={<NewUpgradeSubscribe />} />
                        <Route path="/payment-callback" element={<PaymentCallback />} />
                        <Route path="/continue-to-settings" element={<ContinueToSettings />} />
                    </Route>

                    {/* Vendor Layout */}
                    <Route
                        path="/vendor"
                        element={
                            <ProtectedRoute allowedRoles={["business", "mall"]}>
                                <LayoutProvider />
                            </ProtectedRoute>
                        }
                    >
                        <Route index element={<Navigate replace to="dashboard" />} />
                        <Route path="dashboard" element={<ServiceProvider />} />
                        <Route path="edit-profile" element={<EditProfile />} />
                        <Route path="reviews" element={<Reviews />} />
                        <Route path="order-nfc" element={<NfcOrders />} />
                        <Route path="messages" element={<Messages />} />
                        <Route path="notifications" element={<Notifications />} />
                        <Route path="main-setting" element={<MainSetting />} />

                        {/* Branches */}
                        <Route path="contact/:type" element={<ContactBranches />} />
                        <Route path="add-branch" element={<AddBranch />} />
                        <Route path="edit-branch/:slug" element={<EditBranch />} />
                        <Route path="contact-branch/:slug" element={<ContactBranch />} />
                        <Route path="time-work" element={<TimeWork />} />
                        <Route path="contact-numbers" element={<ContactNumbers />} />
                        <Route path="add-number/:type" element={<AddNumber />} />

                        {/* Menu Sections */}
                        <Route path="sections" element={<Sections />} />
                        <Route path="add-section" element={<AddSection />} />
                        <Route path="edit-section/:slug" element={<EditSection />} />
                        <Route path="section/:slug" element={<Section />} />
                        <Route path="add-category" element={<AddCategory />} />
                        <Route path="edit-category/:slug" element={<EditCategory />} />

                        {/* Mall Shops */}
                        <Route path="shops-sections" element={<ShopsSections />} />
                        <Route path="add-shop-section" element={<AddShopSection />} />
                        <Route path="edit-shop-section/:slug" element={<EditShopSection />} />
                        <Route path="shops-section/:slug" element={<ShopsSection />} />
                        <Route path="add-shop" element={<AddShop />} />
                        <Route path="edit-shop/:slug" element={<EditShop />} />
                        <Route path="shops/offers" element={<ShopsMallOffers />} />

                        {/* Links */}
                        <Route path="links" element={<Links />} />
                        <Route path="add-link/:type" element={<AddLink />} />
                        <Route path="library-links" element={<LibraryLinks />} />
                        <Route path="add-url-link/:type" element={<AddUrlLink />} />
                        <Route path="add-custom-url-link" element={<AddCustomUrlLink />} />
                        <Route path="edit-url-link/:slug" element={<EditUrlLink />} />
                        <Route path="edit-custom-url-link/:slug" element={<EditCustomUrlLink />} />

                        <Route path="customer-activities" element={<CustomerActivities />} />

                        {/* Offers */}
                        <Route path="offers" element={<Offers />} />
                        <Route path="add-offer" element={<AddOffer />} />
                        <Route path="edit-offer/:slug" element={<EditOffer />} />

                        {/* Features */}
                        <Route path="more-features" element={<MoreFeatures />} />
                        <Route path="super-faster-features" element={<SuperFasterFeatures />} />
                        <Route path="add-effect-faster" element={<AddEffectFaster />} />
                        <Route path="add-open-message" element={<AddOpenMessage />} />
                        <Route path="add-marquee" element={<AddMarquee />} />
                        <Route path="add-animated-image" element={<AddAnimatedImage />} />
                        <Route path="link-statistics" element={<LinkStatistics />} />
                        <Route path="shopping-cart" element={<ShoppingCart />} />

                        {/* Settings */}
                        <Route path="global-setting" element={<GlobalSetting />} />
                        <Route path="contact-us" element={<ContactUs />} />
                        <Route path="networking-setting" element={<NetworkingSetting />} />
                    </Route>

                    {/* Shop Layout */}
                    <Route
                        path="/shop"
                        element={
                            <ProtectedRoute allowedRoles={["sub_business"]}>
                                <LayoutShop />
                            </ProtectedRoute>
                        }
                    >
                        <Route index element={<Navigate replace to="offers" />} />
                        <Route path="offers" element={<OffersShop />} />
                        <Route path="add-offer" element={<AddOfferShop />} />
                        <Route path="edit-offer/:slug" element={<EditOfferShop />} />
                        <Route path="edit-profile" element={<EditProfile />} />
                        <Route path="contact-us" element={<ContactUs />} />
                    </Route>

                    {/* Marketer Layout */}
                    <Route
                        path="/marketer"
                        element={
                            <ProtectedRoute allowedRoles={['marketer']}>
                                <LayoutMarketer />
                            </ProtectedRoute>
                        }
                    >
                        <Route index element={<Navigate replace to="dashboard" />} />
                        <Route path="dashboard" element={<Home />} />
                        <Route path="main-setting" element={<MainSettingMarketer />} />
                        <Route path="notifications" element={<MarketerNotifications />} />
                        <Route path="edit-profile" element={<EditProfile />} />
                        <Route path="receiving-commission" element={<ReceivingCommission />} />
                        <Route path="commissions" element={<Commissions />} />
                        <Route path="paid-amounts" element={<PaidAmountsDetails />} />
                        <Route path="withdraw-amount" element={<WithdrawAmount />} />
                        <Route path="stores" element={<Stores />} />
                        <Route path="contact-us" element={<ContactUs />} />
                    </Route>

                    {/* 404 */}
                    <Route path="*" element={<NotFound />} />
                </Routes>
            </Suspense>
        </>
    );
};

export default RoutesPath;
